import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import { dashboardWidgetIconSx } from "components/Analytics/dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import {
  GetServiceByVehicleTypeResponse,
  ServiceByVehicleTypeService,
} from "subModule/src/services/analytics/interface";
import { useGetServiceByVehicleTypeService } from "subModule/src/services/analytics/useAnalytics";

interface ServiceByVehicleTypeHeatmapProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
  stationsLoaded: boolean;
}

/** Soft mint → emerald scale matching the design reference */
const getHeatmapColor = (
  value: number,
  min: number,
  max: number,
): { bg: string; text: string } => {
  if (value <= 0) {
    return { bg: "#F3FBF6", text: "#1E3A5F" };
  }
  const ratio = max === min ? 1 : (value - min) / (max - min);
  // Pale mint → soft mint → medium green → deep emerald
  if (ratio < 0.25) return { bg: "#E8F8EE", text: "#1E3A5F" };
  if (ratio < 0.45) return { bg: "#C6EFD6", text: "#1E3A5F" };
  if (ratio < 0.65) return { bg: "#7DDBA3", text: "#0F2A1A" };
  if (ratio < 0.85) return { bg: "#2FA866", text: "#FFFFFF" };
  return { bg: "#0B7A45", text: "#FFFFFF" };
};

export default function ServiceByVehicleTypeHeatmap(
  props: ServiceByVehicleTypeHeatmapProps,
) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const [services, setServices] = useState<ServiceByVehicleTypeService[]>([]);
  const [dateRange, setDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const { refetch: apiRefetch } = useGetServiceByVehicleTypeService({
    stationId: props.globalFilters.stationId?.value || null,
    range: props.globalFilters.range?.value,
    allStation: props.globalFilters.allStation,
  });

  const sortedServices = useMemo(
    () =>
      [...services]
        .map((service) => ({
          ...service,
          vehicleTypes: (service.vehicleTypes || []).filter(
            (vt) => (vt.count || 0) > 0 && vt.vehicleTypeName,
          ),
        }))
        .filter((service) => service.vehicleTypes.length > 0)
        .sort((a, b) => (b.totalCount || 0) - (a.totalCount || 0)),
    [services],
  );

  const vehicleTypes = useMemo(() => {
    const totals = new Map<string, number>();
    sortedServices.forEach((service) => {
      service.vehicleTypes.forEach((vt) => {
        totals.set(
          vt.vehicleTypeName,
          (totals.get(vt.vehicleTypeName) || 0) + (vt.count || 0),
        );
      });
    });
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, total]) => ({ name, total }));
  }, [sortedServices]);

  const cellValues = useMemo(() => {
    const values: number[] = [];
    sortedServices.forEach((service) => {
      service.vehicleTypes.forEach((vt) => {
        if (vt.count > 0) values.push(vt.count);
      });
    });
    return values;
  }, [sortedServices]);

  const minCount = cellValues.length ? Math.min(...cellValues) : 0;
  const maxCount = cellValues.length ? Math.max(...cellValues) : 0;

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetServiceByVehicleTypeResponse | unknown | any) => {
        if (res?.data?.success) {
          const payload = res?.data?.data || {};
          setServices(payload.topServices || []);
          setDateRange(payload.dateRange || null);
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied) props.setGlobalFilterApplied(false);
      });
  };

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded) {
      firstRender.current = false;
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stationsLoaded]);

  useEffect(() => {
    if (props.globalFilterApplied) {
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const hasData = sortedServices.length > 0 && vehicleTypes.length > 0;

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "8px",
        width: "100%",
        height: "100%",
        maxHeight: { xs: 360, md: 340 },
        display: "flex",
        flexDirection: "column",
        border: "1px solid #E5E7EB",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "12px !important",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      <CardContent>
        <Box
          className="flex items-center gap-1.5 mb-2.5"
          sx={{ flexShrink: 0 }}
        >
          <Box
            sx={{
              ...dashboardWidgetIconSx,
              width: 32,
              height: 32,
              borderRadius: "10px",
              
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: props.themes?.primary + "18" || "#2563EB18",
              flexShrink: 0,
            }}
          >
            <DirectionsCarFilledIcon
              sx={{ fontSize: 17, color: props.themes?.primary || "#2563EB" }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              className="font-Saira text-left"
              sx={{
                fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                color: "#0F2744",
                lineHeight: 1.2,
              }}
            >
              {t("servicesByVehicleType")}
            </Typography>
            {dateRange && (
              <Typography
                className="font-Saira text-left"
                sx={{ fontSize: 10, color: "#9E9E9E", mt: 0.25 }}
              >
                {dateRange.from} — {dateRange.to}
              </Typography>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : !hasData ? (
          <Box className="flex justify-center flex-col py-6">
            <Typography
              className="font-Saira text-center italic font-semibold"
              sx={{ color: props.themes?.primary }}
            >
              {t("noDataAvailable")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              minWidth: 0,
              overflow: "auto",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D1D5DB",
                borderRadius: 8,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                minWidth: 0,
                width: "100%",
              }}
            >
              {/* Fixed-width service labels */}
              <Box
                sx={{
                  flex: "0 0 136px",
                  width: 136,
                  maxWidth: 136,
                  pr: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  backgroundColor: "#fff",
                  borderRight: "1px solid #F0F2F5",
                }}
              >
                <Box sx={{ minHeight: 30, flexShrink: 0 }} />
                {sortedServices.map((service) => (
                  <Tooltip
                    key={`label-${service.serviceId}`}
                    followCursor
                    title={service.serviceName}
                  >
                    <Box
                      sx={{
                        minHeight: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        pr: 0.5,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#0F2744",
                          textAlign: "left",
                          lineHeight: 1.25,
                          width: "100%",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {service.serviceName}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>

              {/* Vehicle types + cells */}
              <Box sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${vehicleTypes.length}, minmax(52px, 1fr))`,
                    gap: "6px",
                    alignItems: "center",
                    minWidth: Math.max(vehicleTypes.length * 56, 240),
                  }}
                >
                  {vehicleTypes.map((vt) => (
                    <Tooltip key={vt.name} followCursor title={vt.name}>
                      <Typography
                        className="font-Saira"
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          backgroundColor: "#fff",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#1E3A5F",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: 0,
                          minHeight: 30,
                          px: 0.25,
                          py: 0.5,
                        }}
                      >
                        {vt.name}
                      </Typography>
                    </Tooltip>
                  ))}

                  {sortedServices.map((service) => (
                    <Fragment key={service.serviceId}>
                      {vehicleTypes.map((vt) => {
                        const cell = service.vehicleTypes.find(
                          (item) => item.vehicleTypeName === vt.name,
                        );
                        const count = cell?.count || 0;
                        const colors = getHeatmapColor(
                          count,
                          minCount,
                          maxCount,
                        );
                        return (
                          <Tooltip
                            key={`${service.serviceId}-${vt.name}`}
                            followCursor
                            title={`${service.serviceName} × ${vt.name}: ${count}`}
                          >
                            <Box
                              sx={{
                                backgroundColor: colors.bg,
                                borderRadius: "10px",
                                width: "100%",
                                minWidth: 0,
                                minHeight: 36,
                                height: 36,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 0.25,
                              }}
                            >
                              <Typography
                                className="font-Saira"
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: colors.text,
                                  lineHeight: 1,
                                }}
                              >
                                {count}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Fragment>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
